import { FC, PropsWithChildren, useMemo } from 'react';

import {
    BoothLoginScreen,
    BoothUserEmailConfirmationScreen,
    BoothUserLoginScreen,
    UserChangePasswordScreen,
    UserRegisterScreen,
    UserResetPasswordScreen,
} from '@auth-v2';
import { ActivityLoader, ParamsLoader, SwingLoader, UserLoader } from '@loaders';
import { KioskConnection } from '@realtime';
import { useAtomValue } from 'jotai';
import { Navigate, Outlet, Route, Routes } from 'react-router';

import { useActivitySwings } from '@queries';
import { ActivityReport, GoalReport, LoadingScreen, PlaybackScreen, UserWelcomeWizardProvider } from '@screens';
import { currentActivityIdAtom, isCurrentActivityAtom, useEnsureAtomValue } from '@store';

import {
    ActivityModalLauncher,
    BackToFullActivityButton,
    NavigateToCurrentActivity,
    SwingModalLauncher,
} from '../components';
import {
    ActivityCreateScreen,
    ActivityOverviewScreen,
    ClubSelectionScreen,
    CurrentActivityScreen,
    Playback3DScreen,
    SwingDetailsScreen,
    SwingFoundationsEditorScreen,
    TagSelectionScreen,
    UserSettingsScreen,
} from '../screens';
import { LiveAnalysisScreen } from '../screens/LiveAnalysisScreen';
import { StudentSettingsScreen } from '../screens/StudentSettingsScreen';
import { LINKS, PARAMETERS, ROUTES } from '../utils/routes';

import { SessionInactivityManager } from './SessionInactivityManager';

const BoothConnectionProvider: FC<PropsWithChildren> = ({ children }) => {
    const currentActivityId = useEnsureAtomValue(currentActivityIdAtom);
    return <KioskConnection currentActivityId={currentActivityId}>{children}</KioskConnection>;
};

const NewSwingOrRedirect = () => {
    const isCurrentActivity = useAtomValue(isCurrentActivityAtom);
    // The index route can keep rendering with the previous activity match while
    // navigation to a newly created activity is pending, leaving its route context stale.
    // Temporary workaround: read the activity ID from the current browser URL.
    const activityId = window.location.pathname.match(/^\/(\d+)(?:\/|$)/)?.[1];

    const { data: activitySwings, isLoading } = useActivitySwings(Number(activityId));
    const firstSwingId = activitySwings?.[0]?.uuid;

    if (!activityId) throw new Error('No activityId param');
    if (isLoading) return <LoadingScreen message="Loading activity..." />;
    if (isCurrentActivity && firstSwingId) return <Navigate to={`${PARAMETERS.SWING}/${firstSwingId}`} />;
    if (isCurrentActivity) return <SwingDetailsScreen />;
    return <Navigate to={firstSwingId ? `${PARAMETERS.SWING}/${firstSwingId}` : `${PARAMETERS.LIVE_ANALYSIS}`} />;
};

export const BoothRouter = () => {
    const activityRoutes = useMemo(
        () => (
            <>
                <Route path={PARAMETERS.OVERVIEW} element={<ActivityOverviewScreen />} />
                <Route path={PARAMETERS.USER_SETTINGS} element={<UserSettingsScreen />} />
                <Route path={PARAMETERS.STUDENT_SETTINGS} element={<StudentSettingsScreen />} />
                <Route path={PARAMETERS.CLUBS} element={<ClubSelectionScreen />} />
                <Route path={PARAMETERS.TAGS} element={<TagSelectionScreen />} />
                <Route path={PARAMETERS.PARAMETERS} element={<SwingFoundationsEditorScreen />} />
                <Route path={PARAMETERS.REPORT} element={<ActivityReport getLinkBack={LINKS.getFullActivity} />} />
                <Route
                    path={`${PARAMETERS.GOAL_REPORT}/${PARAMETERS.LAYOUT_ID}`}
                    element={
                        <GoalReport
                            getFullActivityRoute={LINKS.getFullActivity}
                            backButton={<BackToFullActivityButton />}
                        />
                    }
                />
            </>
        ),
        [],
    );

    const swingRoutes = useMemo(
        () => (
            <>
                <Route path={PARAMETERS.PLAYBACK}>
                    <Route index element={<PlaybackScreen />} />
                    <Route path={PARAMETERS.PLAYBACK_COMPARISON} element={<PlaybackScreen />} />
                </Route>
                <Route path={PARAMETERS.VISUALIZER_3D} element={<Playback3DScreen />} />
            </>
        ),
        [],
    );

    return (
        <Routes>
            <Route
                path="/"
                element={
                    <ParamsLoader>
                        <UserLoader>
                            <BoothConnectionProvider>
                                <UserWelcomeWizardProvider>
                                    <SessionInactivityManager />
                                    <Outlet />
                                </UserWelcomeWizardProvider>
                            </BoothConnectionProvider>
                        </UserLoader>
                    </ParamsLoader>
                }
            >
                <Route index element={<CurrentActivityScreen />} />

                {/* Resolve the current activity on entry, or send the user to create one. */}
                <Route path={PARAMETERS.CURRENT_ACTIVITY} element={<CurrentActivityScreen />} />
                <Route path={PARAMETERS.NEW_ACTIVITY} element={<ActivityCreateScreen />} />
                <Route
                    path={PARAMETERS.ACTIVITY_ID}
                    element={
                        <ActivityLoader errAction={<NavigateToCurrentActivity />}>
                            <Outlet />
                            <ActivityModalLauncher />
                        </ActivityLoader>
                    }
                >
                    <Route index element={<NewSwingOrRedirect />} />
                    <Route path={PARAMETERS.LIVE_ANALYSIS} element={<LiveAnalysisScreen />} />
                    {activityRoutes}
                    <Route
                        path={`${PARAMETERS.SWING}/${PARAMETERS.SWING_ID}`}
                        element={
                            <SwingLoader errAction={<NavigateToCurrentActivity />}>
                                <Outlet />
                                <SwingModalLauncher />
                            </SwingLoader>
                        }
                    >
                        <Route index element={<SwingDetailsScreen />} />
                        {activityRoutes}
                        {swingRoutes}
                    </Route>
                </Route>
            </Route>
            <Route
                path={ROUTES.LOGIN}
                element={
                    <BoothUserLoginScreen registerPath={ROUTES.REGISTER} resetPasswordPath={ROUTES.RESET_PASSWORD} />
                }
            />
            <Route path={ROUTES.REGISTER} element={<UserRegisterScreen confirmationPath={ROUTES.CONFIRM} />} />
            <Route path={ROUTES.CONFIRM} element={<BoothUserEmailConfirmationScreen />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<UserResetPasswordScreen />} />
            <Route path={ROUTES.CHANGE_PASSWORD} element={<UserChangePasswordScreen />} />
            <Route path={ROUTES.BOOTH_LOGIN} element={<BoothLoginScreen />} />
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
};
